import React from 'react';
import UpdateLayout from './UpdateLayout';
import {
  DELIVERY_CONTEXT,
  SQLALCHEMY_UPGRADE_AS_OF,
  SQLALCHEMY_UPGRADE_SOURCES,
} from '../../../content/snowflakeUpdates';

const codeClass =
  'overflow-x-auto rounded-xl border border-ink-200 bg-surface-subtle p-4 text-sm leading-relaxed text-ink-800';

export default function SqlAlchemyUpgradePost() {
  return (
    <UpdateLayout
      path="/updates/snowflake-sqlalchemy-2-upgrade-checklist"
      title="Upgrading to Snowflake SQLAlchemy 2.0 and Python Connector 5.0: a checklist"
      asOf={SQLALCHEMY_UPGRADE_AS_OF}
      sources={SQLALCHEMY_UPGRADE_SOURCES}
    >
      <p>
        Snowflake SQLAlchemy 2.0.0 was released on October 19, 2026. It is a major release with
        breaking changes. The dialect requires {'SQLAlchemy >=2.0.44,<2.1'}, Snowflake Python
        Connector 5.0 or newer, and Python 3.11 to 3.14. Python Connector 5.0.0 requires Python
        3.11 or later. {DELIVERY_CONTEXT} Governed data stays in the customer&apos;s account.
      </p>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        Who is affected
      </h2>
      <p>
        Check unpinned Python stacks, dbt-adjacent tooling, Superset, and Alembic migrations. If{' '}
        <code>snowflake-sqlalchemy</code> is not pinned, the next resolve can install 2.0.0.
      </p>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        Breaking changes
      </h2>
      <ul className="list-disc space-y-2 pl-5">
        <li>JSON-type columns return dicts or lists by default.</li>
        <li>Integer {'//'} now floors.</li>
        <li>
          <code>force_div_is_floordiv</code> and <code>legacy_url_params</code> are removed.
        </li>
      </ul>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        New in 2.0.0
      </h2>
      <ul className="list-disc space-y-2 pl-5">
        <li>Async engine</li>
        <li>Native UUID</li>
        <li>COLLATE</li>
        <li>InsertMulti</li>
        <li>Row access policies</li>
        <li>RELY constraints</li>
      </ul>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        Checklist
      </h2>
      <ol className="list-decimal space-y-3 pl-5">
        <li>
          <span className="font-semibold text-ink-900">Inventory where snowflake-sqlalchemy is installed.</span>{' '}
          Search the Python environments that talk to the warehouse, including dbt-adjacent tooling,
          Superset, and Alembic migrations.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Pin snowflake-sqlalchemy below 2.0.0 now.</span>{' '}
          Hold the current line while you test the major release on a non-prod branch.
          <pre className={`${codeClass} mt-3`}>
            <code>snowflake-sqlalchemy&lt;2.0.0</code>
          </pre>
        </li>
        <li>
          <span className="font-semibold text-ink-900">Check the Python version.</span> SQLAlchemy
          2.0.0 supports Python 3.11 to 3.14. Python Connector 5.0.0 requires Python 3.11 or later.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Upgrade the Python Connector to 5.0.</span> Do
          this in a non-prod branch, against a non-prod Snowflake account.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Test JSON column handling and integer division.</span>{' '}
          JSON-type columns return dicts or lists by default, and integer {'//'} now floors.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Remove the deprecated parameters.</span> Delete{' '}
          <code>force_div_is_floordiv</code> and <code>legacy_url_params</code> from engine
          configuration. Both are removed in 2.0.0.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Run CI against a non-prod Snowflake account.</span>{' '}
          Use Azure DevOps Pipelines or GitHub Actions. The account under test should not be
          production.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Promote through your normal release process.</span>{' '}
          Move the tested upgrade on the same release path as the rest of the data infrastructure.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Smoke-test Airflow and dbt jobs.</span> Run the
          jobs that import this stack after the promotion, and confirm they still reach the warehouse
          in the customer&apos;s account.
        </li>
      </ol>
    </UpdateLayout>
  );
}
